import * as threeDtools from 'src/app/classes/shared/threeDTools';

export class MarkerTool {
    polyPoints: any[]
    zAxis
    updateZone
    undoList: any[]
    linesOverlay
    sphereMaterial
    cubeMaterial
    startingSphere: THREE.Object3D[]
    refMeshes: { cube: THREE.Mesh; sphere: THREE.Mesh | undefined; }[]
    sphereRadius
    lineWidth
    toolName: string = "marker-tool";
    viewer;
    origin;
    projectXY;
    offset = {
        x: 0,
        y: 0,
        z: 0
    };
    dbIds: any;
    marks: any[] = [];

    constructor(params: { viewer: any; model: { origin: any; projectXY: any; }; updateZone: any; }) {
        this.viewer = params.viewer;
        this.origin = params.model.origin;
        let offset = this.viewer.model.getData().globalOffset;

        if (offset)
            this.offset = offset;

        this.projectXY = params.model.projectXY;
        this.viewer = params.viewer;
        this.polyPoints = [];
        this.zAxis = this.origin.position.z;
        this.updateZone = params.updateZone;
        this.undoList = [];
        this.linesOverlay = "zoneLinesOverlay";
        this.sphereMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
        this.cubeMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
        this.startingSphere = [];
        this.refMeshes = [];
        let scale = params.model && params.model.origin ? params.model.origin.scale : 1;
        this.sphereRadius = scale;
        this.lineWidth = 0.2 * scale;

        this.viewer.addEventListener(
            Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, () => {
                this.dbIds = this.getAllDbIds()
            });
    }

    public activate(): any { }

    public deactivate(): any { }

    getAllDbIds() {
        const { instanceTree } = this.viewer.model.getData()
        const { dbIdToIndex } = instanceTree.nodeAccess

        return Object.keys(dbIdToIndex).map((dbId) => {
            return parseInt(dbId)
        })
    }

    getPolyPoints() {
        return this.polyPoints;
    };

    getNames() {
        return [this.toolName];
    };

    getName() {
        return this.toolName;
    };

    undoLastMark() {
        var point = this.polyPoints.pop();
        this.undoList.push(point);

        var meshRef = this.refMeshes.pop();

        if (meshRef && meshRef.cube) {
            this.viewer.impl.overlayScenes[this.linesOverlay].scene.remove(meshRef.cube);
        }

        if (meshRef && meshRef.sphere != undefined) {
            this.viewer.impl.overlayScenes[this.linesOverlay].scene.remove(meshRef.sphere);
        }

        if (this.polyPoints.length == 0) {
            this.removeLines();
        }

        this.viewer.impl.invalidate(true, true, true);
        return true;
    };

    redoLastMark() {
        if (this.undoList.length) {
            var point = this.undoList.pop();
            this.polyPoints.push(point);
            this.drawLines();
        }
    }

    finish() {
        this.removeLines();

        var pointTransformed = [],
            pointLocal = [];

        for (var i = 0; i < this.polyPoints.length; i++) {
            pointLocal.push([
                this.polyPoints[i].x,
                this.polyPoints[i].y
            ]);

            let point = threeDtools.reverseTransform2({
                 x: this.polyPoints[i].x, y: this.polyPoints[i].y, z: 0 },  
                 this.origin, this.offset);

            pointTransformed.push([
                point.x,
                point.y
            ]);
        }

        return { transformed: pointTransformed.slice(), local: pointLocal.slice() };
    };

    pointerToRaycaster(domElement: { getBoundingClientRect: () => any; }, camera: any, pointer: { clientX: number; clientY: number; }) {
        const pointerVector = new THREE.Vector3()
        const pointerDir = new THREE.Vector3()
        const ray = new THREE.Raycaster()

        const rect = domElement.getBoundingClientRect()

        const x = ((pointer.clientX - rect.left) / rect.width) * 2 - 1
        const y = -((pointer.clientY - rect.top) / rect.height) * 2 + 1

        if (camera.isPerspective) {
            pointerVector.set(x, y, 0.5)
            pointerVector.unproject(camera)

            if (camera.position) {
                ray.set(camera.position, pointerVector.sub(camera.position).normalize())
            }

        } else {
            pointerVector.set(x, y, -1)
            pointerVector.unproject(camera)
            pointerDir.set(0, 0, -1)

            ray.set(pointerVector, pointerDir.transformDirection(camera.matrixWorld))
        }

        return ray
    }

    handleSingleClick(event: { pointers: any[]; canvasX: any; canvasY: any; }, button: number) {
        if (button === 0) {
            const pointer = event.pointers ? event.pointers[0] : event
            const rayCaster = this.pointerToRaycaster(this.viewer.impl.canvas, this.viewer.impl.camera, pointer)
            const intersectResults = rayCaster.intersectObjects(this.startingSphere, true)

            const hitTest = this.viewer.model.rayIntersect(rayCaster, true, this.dbIds)
            const selections = intersectResults.filter((res) => (!hitTest || (hitTest.distance > res.distance)))
            let isStartingPoint: boolean = false;

            if (selections.length) {
                this.polyPoints.push(this.polyPoints[0]);
                isStartingPoint = true;
            } else {
                var vpVec = this.viewer.impl.clientToViewport(event.canvasX, event.canvasY);
                var point = this.viewer.impl.intersectGroundViewport(vpVec);
                this.polyPoints.push(point);
            }

            this.drawLines(isStartingPoint);
        }

        return false;
    };

    removeLines() {
        this.viewer.impl.removeOverlayScene(this.linesOverlay);
        this.viewer.impl.removeOverlayScene('startingSphere');
        this.viewer.impl.invalidate(true, true, true);
    };

    drawLines(isStartingPoint: boolean | null = null) {
        if (this.polyPoints.length == 0) return;

        if (this.polyPoints.length == 1) {
            let sphereGeometry = new THREE.SphereGeometry(this.sphereRadius, 32, 32);
            let sphere = new THREE.Mesh(new THREE.BufferGeometry().fromGeometry(sphereGeometry), this.sphereMaterial.clone());
            sphere.position.set(this.polyPoints[0].x, this.polyPoints[0].y, this.zAxis);

            this.startingSphere.push(sphere);
            this.viewer.impl.createOverlayScene('startingSphere', this.sphereMaterial);
            this.viewer.impl.createOverlayScene(this.linesOverlay, this.sphereMaterial);
            this.viewer.impl.addOverlay('startingSphere', sphere);

            return;
        }

        let points = [];
        for (var i = 0; i < this.polyPoints.length; i++) {
            points.push(new THREE.Vector3(this.polyPoints[i].x, this.polyPoints[i].y, this.zAxis));
        }

        let sphereGeometry = new THREE.SphereGeometry(this.sphereRadius, 32, 32);
        const prev = points[points.length - 2];
        const current = points[points.length - 1];
        if (!prev || !current) return;
        let sphere;

        if (!isStartingPoint) {
            sphere = new THREE.Mesh(new THREE.BufferGeometry().fromGeometry(sphereGeometry), this.sphereMaterial.clone());
            sphere.position.set(current.x, current.y, current.z);
            this.viewer.impl.addOverlay(this.linesOverlay, sphere);
        }

        const width = this.lineWidth;
        const height = this.lineWidth;
        const length = prev.distanceTo(current);

        let cubeGeometry = new THREE.BoxGeometry(width, height, length);
        cubeGeometry.applyMatrix(new THREE.Matrix4().makeTranslation(0, 0, length / 2));

        let cube = new THREE.Mesh(new THREE.BufferGeometry().fromGeometry(cubeGeometry), this.cubeMaterial.clone());
        cube.position.copy(prev);
        cube.lookAt(current);

        this.viewer.impl.addOverlay(
            this.linesOverlay, cube);

        this.refMeshes.push({ cube, sphere });
        this.viewer.impl.invalidate(true, true, true);
    };

    removeAllMarks() {
        this.marks.forEach(mark => {
            if (mark && typeof mark.remove === 'function') {
                mark.remove();
            }
        });
        this.marks = [];
    }
}